import { APP_BASE_HREF } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, NgZone, OnInit, Renderer2, DOCUMENT } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { InitialServices } from 'src/app/_services/initial.services';
import * as _ from 'lodash';
import { forkJoin, Subscription, timer } from 'rxjs';
import * as Cookies from 'es-cookie';
import { ThemeId, ThemeService } from '../theme.service';
import { EmployeeServices } from './../kuba/employees/services/employee.services';
import { CacheHandlerService } from './../kuba/cached.services';
import { NetworkConnection, ConnectionStatusEnum } from './connection.check';
import { KubaServices } from '../kuba/kuba.services';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from '../_services/authentication.service';
import { AlertService } from '../_services/alert.service';
import { IdentityToken } from "../_models/identityToken";
import { TranslateService } from '@ngx-translate/core';
import { isAgilPlatform, isDinHmsPlatform } from '../shared/common/common-functions.util';


@Component({
  templateUrl: 'login.component.html',
  styleUrls: ['login.component.css', 'login-aura.css', 'mdco-auth.scss'],
  standalone: false
})
export class LoginComponent implements OnInit {
  accesslog: any;
  model: any = {};
  loading = false;
  readonly animateEntrance: boolean;
  readonly highlightKeys = ['LOGIN_DESIGN.HIGHLIGHT_OVERVIEW', 'LOGIN_DESIGN.HIGHLIGHT_FOLLOW_UP', 'LOGIN_DESIGN.HIGHLIGHT_PEOPLE'];
  highlightIndex = 0;
  highlightsPaused = false;
  canRotateHighlights = false;
  private highlightTimer?: Subscription;
  private highlightMedia?: MediaQueryList;
  private readonly onHighlightMediaChange = () => {
    this.canRotateHighlights = this.highlightMedia?.matches === true;
    this.updateHighlightRotation();
  };
  returnUrl: any;
  baseUrl: any;
  userSettings: any;
  deviceInfo = null;
  isInvalid = false;
  userName: any;
  password: any;
  isPhp = false;
  displayDialog = false;
  IsTOCApproved: any;
  userRole: any;
  assetloc: string = environment.BASE_STYLES_PATH;
  isOnline = true;
  isMedi3 = false;
  isSvenn = false;
  /**
   * Agil Helse's own login, served at /agil/login.
   *
   * Read from the platform prefix rather than the pathname, so it agrees with
   * APP_BASE_HREF and cannot be set off by a business name that happens to
   * contain the word — the substring checks below are the older, looser style.
   */
  isAgil = false;
  currentLang: 'en' | 'no' = 'no';
  termsAndCondition: any;
  csession: any;
  private subscriptions: Subscription[] = [];
  ipAddress: any;
  errorMessage: string;

  get isDinHms(): boolean {
    return isDinHmsPlatform();
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private renderer: Renderer2,
    private themeService: ThemeService,
    private alertService: AlertService,
    private authenticationService: AuthenticationService,
    private kubaServices: KubaServices,
    private initialServices: InitialServices,
    private employeeServices: EmployeeServices,
    private cacheHandlerService: CacheHandlerService,
    @Inject(APP_BASE_HREF) private baseHref: string,
    @Inject(DOCUMENT) private document: any,
    private http: HttpClient,
    private translate: TranslateService,
    private ngZone: NgZone
  ) {
    // Capture once: Router.navigated changes when this initial navigation finishes.
    this.animateEntrance = !this.router.navigated;
    this.isPhp = <boolean>this.route.snapshot.data['phpLogin'];
    this.getIPAddress();
  }

  ngOnInit() {
    // reset login status
    sessionStorage.clear();
    this.resetTheme();
    this.initialServices.appInitializing();
    // get return url from route parameters or default to '/app'
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/app';
    this.renderer.addClass(document.body.parentElement, 'loginView');
    this.renderer.addClass(document.body, 'loginView');
    let medi3 = window.location.pathname.substring(
      window.location.pathname.indexOf('medi3') + 0,
      window.location.pathname.indexOf('medi3') + 5
    );
    let svenn = window.location.pathname.substring(
      window.location.pathname.indexOf('svenn') + 0,
      window.location.pathname.indexOf('svenn') + 5
    );
    this.isAgil = isAgilPlatform();
    if (medi3 == 'medi3') {
      this.isMedi3 = true;
      this.termsAndCondition = 'Medi3 - Vilkår og betingelser';
    } else if (svenn == 'svenn') {
      this.isSvenn = true;
      this.termsAndCondition = 'Svenn - Vilkår og betingelser';
    } else {
      this.isMedi3 = false;
      this.termsAndCondition = 'Kuba - Vilkår og betingelser';
    }
    this.setLanguage('no');
    // Agil's hero carries its own standing tagline instead of the rotating
    // Kuba phrases, so there is nothing to rotate there.
    if (!this.isMedi3 && !this.isSvenn && !this.isPhp && !this.isAgil) {
      this.highlightMedia = this.document.defaultView.matchMedia(
        '(min-width: 901px) and (prefers-reduced-motion: no-preference)'
      );
      this.highlightMedia.addEventListener('change', this.onHighlightMediaChange);
      this.onHighlightMediaChange();
    }
  }

  pauseHighlights(): void {
    this.highlightsPaused = true;
    this.highlightTimer?.unsubscribe();
  }

  private updateHighlightRotation(): void {
    this.highlightTimer?.unsubscribe();
    if (this.highlightsPaused || !this.canRotateHighlights) return;
    // This decorative timer must not hold Angular's application stability open.
    this.ngZone.runOutsideAngular(() => {
      this.highlightTimer = timer(6000, 6000).subscribe(() => {
        if (this.document.hidden || this.isMedi3 || this.isSvenn || this.isPhp) return;
        this.ngZone.run(() => {
          this.highlightIndex = (this.highlightIndex + 1) % this.highlightKeys.length;
        });
      });
    });
  }

  setLanguage(lang: 'en' | 'no') {
    this.currentLang = lang;
    this.translate.use(lang);
    localStorage.setItem('languageId', lang === 'en' ? '1' : '2');
    localStorage.setItem('languageMode', lang === 'en' ? 'en-GB' : 'no-NO');
    this.document.documentElement.lang = lang;
  }

  getIPAddress() {
    this.http.get(`${environment.BASE_URL}/get-ip`).subscribe((ipAdress: any) => {
      this.ipAddress = ipAdress.ip || '';
    });
  }
  resetTheme(): void {
    const themePath = ''; // or themePath = null;
    this.themeService.setTheme(ThemeId.DEFAULT);
  }

  selfRedirect(msg: string) {
    this.alertService.error(msg);
    this.loading = false;
    this.router.navigate(['login']);
  }

  // close pop-up
  decline() {
    this.displayDialog = false;
    this.selfRedirect('GDPR Declined');
  }

  // Accept GDPR agreement
  accept() {
    this.displayDialog = false;
    this.loading = true;

    const userId = this.model.userId;
    const isTOCApproved = true;

    const url = `${environment.BASE_URL}/current-user/${userId}/${isTOCApproved}`;
    this.http.put(url, {}).subscribe(
      (response: any) => {
        this.verifyGDPRandLogin();
      },error => {
        console.error('Error updating GDPR status:', error);
        this.alertService.error('Failed to update GDPR status.');
        this.loading = false;
      }
    );
  }

  verifyGDPRandLogin(): void {
    this.pauseHighlights();
    this.loading = true;
    if (NetworkConnection.status === ConnectionStatusEnum.Offline) {
      this.isOnline = false;
    } else if (NetworkConnection.status === ConnectionStatusEnum.Online) {
      this.isOnline = true;
    }
    this.isPhp = false;

    this.subscriptions.push(
      this.authenticationService
        .login(this.model.username, this.model.password, this.ipAddress)
        .subscribe(
          (res: IdentityToken) => {
            this.model.userId = res.UserId;

            if (res && res.Role !== "" && res.Role !== null) {
              if (!res.IsTocApproved) {
                this.loading = false;
                this.displayDialog = true;
              } else {
                if (res && res.Role !== '' && res.Role !== undefined) {
                  // cache clear
                  this.cacheHandlerService.clearCachedData();
                  // set userSetting for apploication when is not set
                  if (sessionStorage.getItem('userSettings') == null) {
                    let observables = [];

                    // Retrives all available languages
                    observables.push(this.kubaServices.getAllLanguages());

                    // Retrive current user language , UserProfile & UserTheme
                    observables.push(this.kubaServices.getUserCustomSetting());

                    // get and set UserBase
                    observables.push(this.kubaServices.getUserBase());

                    // get and set UserFeatures
                    observables.push(this.kubaServices.getUserFeatures());

                    // Observe above call for completion
                    this.subscriptions.push(
                      forkJoin(observables).subscribe(result => {
                          if (result) {
                            result.forEach((data: any) => {
                              if (data['userLanguageTheme'] != null) {
                                this.userSettings = _.assign(
                                  this.userSettings,
                                  data['userLanguageTheme']
                                );
                                this.userRole = _.assign(
                                  this.userSettings,
                                  data['userBase']
                                );
                              } else {
                                this.userSettings = _.assign(
                                  this.userSettings,
                                  data
                                );
                              }
                            });
                          }
                        },error =>
                          console.error('Error at App Init services : ', error),
                        () => {
                          this.loading = false;
                          // setting Up Session Storage and listen to it
                          sessionStorage.setItem(
                            'userSettings',
                            JSON.stringify(this.userSettings)
                          );

                          // Theme here rather than inside the forkJoin results:
                          // this runs once, after userTheme and userBase have
                          // both been merged, so the application is known. An
                          // application-branded tenant such as Agil has no
                          // Theme row, and is themed by its application id.
                          this.themeService.applyThemeForUser(
                            this.userSettings?.userTheme?.Id,
                            this.userSettings?.userBase?.ApplicationId
                          );

                          if (sessionStorage.getItem('appSettings') == null) {
                            this.loading = false;
                            window.location.href =
                              window.location.href.replace(
                                window.location.pathname,
                                ''
                              ) +
                              '/' +
                              this.userSettings.userBase.BaseUrl;
                            this.router.navigate([this.returnUrl]);
                          } else {
                            this.router.navigate([this.returnUrl], {
                              relativeTo: this.route
                            });
                          }
                        }
                      )
                    );
                  }
                } else {
                  this.loading = false;
                  this.alertService.error('Invalid User or Password');
                }
              }
            } else {
              this.loading = false;
              this.isInvalid = true;
              setTimeout((x: any) => {
                this.isInvalid = false;
              }, 4000);
            }
          },error => {
            this.handleErrorMessages(error);
          }
        )
    );
  }


  login(): void {
    this.subscriptions.push(
      this.authenticationService
        .login(this.model.username, this.model.password, this.ipAddress)
        .subscribe(res => {

          },error => {
            this.loading = false;
            this.isInvalid = true;
            setTimeout((x: any) => {
              this.isInvalid = false;
            }, 2000);
            if (error.status === 0) {
              this.alertService.error('User may not available.');
            }
            if (error.status !== 0) {
              this.alertService.error(error._body);
            }
          }
        )
    );
  }
 
  goToForgetPassword() {
    this.router.navigate(['./../forget-password'], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve'
    });
  }

  RememberMeFunction() {
    if (this.model.rememberMe === true) {
      Cookies.set('UserName', this.model.username, { expires: 60 });
      Cookies.set('Password', this.model.password, { expires: 60 });
    } else {
      Cookies.set('UserName', this.model.username, { expires: -60 });
      Cookies.set('Password', this.model.password, { expires: -60 });
    }
  }

  handleErrorMessages(error){
    switch (error.status) {
      case 0: // Network issue or timeout
        if (error.name === 'TimeoutError') {
          this.errorMessage = this.translate.instant('REQUEST_TIMEOUT');
        } else {
          this.errorMessage = this.translate.instant('SERVER_UNAVAILABLE');
        }
        break;

      case 400: //Bad request
        this.errorMessage = this.translate.instant('WRONG_USERNAME_PASSWORD');
        break;

      case 504: // Gateway timeout
        this.errorMessage = this.translate.instant('SERVER_DOWN');
        break;

      default: // Handle unexpected errors
        this.errorMessage = this.translate.instant('SOMETHING_WENT_WRONG');
        break;
      }
      this.isInvalid = true;
      this.displayDialog = false;
      this.loading = false;
      setTimeout(() =>{
        this.isInvalid = false;
      }, 5000);
  }

  ngOnDestroy() {
    this.highlightTimer?.unsubscribe();
    this.highlightMedia?.removeEventListener('change', this.onHighlightMediaChange);
    this.renderer.removeClass(document.body.parentElement, 'loginView');
    this.renderer.removeClass(document.body, 'loginView');
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
}
